import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import { Badge } from '@/components/ui'
import { formatCurrency } from '@/lib/utils'
import { IconDollar } from '@/components/icons/Icons'

/*
  Hitos de cobro del proyecto. No son un dato propio: se leen del cronograma
  de la cotización vinculada, que es lo que el cliente firmó.
*/
interface Hito {
  cotizacion_numero: string
  numero: number
  descripcion: string
  condicion: string
  monto_con_igv: number
  estado: 'pendiente' | 'facturado' | 'cobrado'
  factura_numero: string | null
}

interface Resumen {
  total: number
  cobrado: number
  por_cobrar: number
  sin_cotizacion: boolean
}

import type { BadgeVariant } from '@/components/ui'

const BADGE: Record<Hito['estado'], { label: string; variant: BadgeVariant }> = {
  cobrado: { label: 'Cobrado', variant: 'completado' },
  facturado: { label: 'Facturado', variant: 'enviada' },
  pendiente: { label: 'Pendiente', variant: 'pausado' },
}

export default function HitosProyecto({ proyectoId, moneda, modeloCobro }: { proyectoId: number; moneda: string; modeloCobro?: string }) {
  const api = useApi()
  const [hitos, setHitos] = useState<Hito[]>([])
  const [resumen, setResumen] = useState<Resumen | null>(null)
  const [loading, setLoading] = useState(true)

  // `api` fuera de las dependencias: useApi() devuelve un objeto nuevo en cada
  // render y dejaría el efecto recargando en bucle.
  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const d = await api.get<{ hitos: Hito[]; resumen: Resumen }>(`/api/proyectos/${proyectoId}/hitos`)
      setHitos(d.hitos || [])
      setResumen(d.resumen)
    } catch (err) {
      console.error('Error cargando hitos:', err)
    } finally {
      setLoading(false)
    }
  }, [proyectoId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cargar() }, [cargar])

  if (loading) return null
  if (!resumen) return null

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <IconDollar size={16} />
        <h3 style={{ margin: 0 }}>Hitos de cobro</h3>
        {!resumen.sin_cotizacion && (
          <span className="text-muted" style={{ marginLeft: 'auto', fontSize: 14 }}>
            Cobrado {formatCurrency(resumen.cobrado, moneda)} de {formatCurrency(resumen.total, moneda)}
            {resumen.por_cobrar > 0 && (
              <> · <b>falta {formatCurrency(resumen.por_cobrar, moneda)}</b></>
            )}
          </span>
        )}
      </div>

      {resumen.sin_cotizacion ? (
        // La causa real tiene arreglo a un clic: se dice, en vez de mostrar un bloque vacío.
        <p className="text-muted" style={{ fontSize: 14 }}>
          Este proyecto no tiene ninguna cotización vinculada, y los hitos salen de ahí.
          Vinculá una desde el bloque de arriba y aparecen solos.
        </p>
      ) : hitos.length === 0 && modeloCobro === 'horas' ? (
        // Un contrato de soporte no tiene hitos: tiene mensualidades. Decirle
        // "falta el cronograma" seria mandarlo a arreglar algo que esta bien.
        <p className="text-muted" style={{ fontSize: 14 }}>
          Contrato mensual: no lleva cronograma de hitos, se factura una cuota por mes
          y por adelantado. Cada mensualidad emitida está en la pestaña <b>Cobros</b>.
        </p>
      ) : hitos.length === 0 ? (
        <p className="text-muted" style={{ fontSize: 14 }}>
          La cotización vinculada no define un cronograma de pagos.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {hitos.map((h, i) => (
            <div
              key={`${h.cotizacion_numero}-${h.numero}-${i}`}
              style={{
                display: 'flex', gap: 14, alignItems: 'flex-start',
                paddingBottom: 10,
                borderBottom: i < hitos.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{h.descripcion}</div>
                {h.condicion && (
                  <div className="text-muted" style={{ fontSize: 13, marginTop: 2 }}>{h.condicion}</div>
                )}
                {h.factura_numero && (
                  <div className="text-muted" style={{ fontSize: 13, marginTop: 2 }}>
                    Factura {h.factura_numero}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontWeight: 700 }}>{formatCurrency(h.monto_con_igv, moneda)}</div>
                <Badge variant={BADGE[h.estado].variant}>{BADGE[h.estado].label}</Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
